import { COLORS } from './common';

export default {
  label: 'Collection',
  name: 'collection',
  component: 'group',
  description: 'Filters, sort, pagination, product item, promotion',
  fields: [
    {
      label: 'Filters',
      name: 'filters',
      component: 'group',
      description: 'Enable, custom filters, show option count',
      fields: [
        {
          label: 'Enable',
          name: 'enabled',
          component: 'toggle',
          toggleLabels: {
            true: 'On',
            false: 'Off',
          },
          defaultValue: true,
        },
        {
          label: 'Show Option Count',
          name: 'showCount',
          component: 'toggle',
          toggleLabels: {
            true: 'On',
            false: 'Off',
          },
          defaultValue: true,
        },
        {
          label: 'Sticky To Top',
          name: 'sticky',
          component: 'toggle',
          description: 'Sticky to top of left column on tablet/desktop',
          toggleLabels: {
            true: 'On',
            false: 'Off',
          },
          defaultValue: true,
        },
        {
          label: 'Filters',
          name: 'filters',
          component: 'group-list',
          description: `A filter's options are automatically determined by the products in the collection.\n\nNote: a filter will be hidden if there are no options, or only has one option, which has a count equal to the total number of products in the collection.`,
          itemProps: {
            label: '{{item.label}}',
          },
          fields: [
            {
              label: 'Shopify Property',
              name: 'source',
              component: 'radio-group',
              direction: 'horizontal',
              variant: 'radio',
              description:
                'Note: Shopify tags must separate name and value with "::", e.g. "colorfilter::Red"',
              options: [
                { label: 'Tag', value: 'tag' },
                { label: 'Option', value: 'option' },
                { label: 'Product Type', value: 'productType' },
              ],
            },
            {
              label: 'Name',
              name: 'name',
              component: 'text',
              description: `Letter casing must be same as name in Shopify.\nTags e.g. "colorfilter", "material"\nOptions e.g. "Color", "Size"\nNot applicable for Product Type`,
            },
            {
              label: 'Label',
              name: 'label',
              component: 'text',
            },
            {
              label: 'Uses Color Swatches',
              name: 'isColor',
              component: 'toggle',
              description:
                'Matches option value with color value set in:\nStorefront Settings > Product > Colors',
              toggleLabels: {
                true: 'On',
                false: 'Off',
              },
            },
            {
              label: 'Default Open',
              name: 'defaultOpen',
              component: 'toggle',
              description:
                'Applicable only to filter dropdowns on tablet and desktop',
              toggleLabels: {
                true: 'On',
                false: 'Off',
              },
            },
            {
              label: 'Order Options By',
              name: 'orderValuesBy',
              component: 'radio-group',
              direction: 'horizontal',
              variant: 'radio',
              options: [
                { label: 'Alphabet', value: 'alphabet' },
                { label: 'Number', value: 'number' },
                { label: 'Custom', value: 'custom' },
              ],
            },
            {
              label: 'Custom Order',
              name: 'customOrder',
              component: 'list',
              field: {
                component: 'text',
              },
            },
          ],
          defaultItem: {
            source: 'tag',
            name: 'tag',
            label: 'Tag',
            isColor: false,
            defaultOpen: false,
            orderValuesBy: 'alphabet',
          },
          defaultValue: [
            {
              source: 'productType',
              name: '',
              label: 'Product Type',
              isColor: false,
              defaultOpen: false,
              orderValuesBy: 'alphabet',
            },
            {
              source: 'option',
              name: 'Color',
              label: 'Color',
              isColor: true,
              defaultOpen: false,
              orderValuesBy: 'alphabet',
            },
            {
              source: 'option',
              name: 'Size',
              label: 'Size',
              isColor: false,
              defaultOpen: false,
              orderValuesBy: 'custom',
              customOrder: [
                'One Size',
                'OS',
                'O/S',
                'XXS',
                'XX-Small',
                'XS',
                'X-Small',
                'S',
                'Small',
                'M',
                'Medium',
                'L',
                'Large',
                'XL',
                'X-Large',
                'XXL',
                'XX-Large',
                'XXXL',
                'XXX-Large',
              ],
            },
          ],
        },
      ],
    },
    {
      label: 'Pagination',
      name: 'pagination',
      component: 'group',
      description: 'Enable, products per load, load type, load text',
      fields: [
        {
          label: 'Enable',
          name: 'enabled',
          component: 'toggle',
          description: 'Loads products in segments',
          toggleLabels: {
            true: 'On',
            false: 'Off',
          },
        },
        {
          label: 'Products Per Load',
          name: 'resultsPerPage',
          component: 'number',
          description:
            'Count includes any promo tiles that may be positioned within each load',
        },
        {
          label: 'Load Type',
          name: 'loadType',
          component: 'radio-group',
          direction: 'horizontal',
          variant: 'radio',
          options: [
            { label: 'Button', value: 'button' },
            { label: 'Infinite Scroll', value: 'infinite' },
          ],
        },
        {
          label: 'Load Text',
          name: 'loadText',
          component: 'text',
        },
        {
          label: 'Load Button Style',
          name: 'buttonStyle',
          component: 'select',
          options: [
            { label: 'Primary', value: 'btn-primary' },
            { label: 'Secondary', value: 'btn-secondary' },
            { label: 'Inverse Light', value: 'btn-inverse-light' },
            { label: 'Inverse Dark', value: 'btn-inverse-dark' },
          ],
        },
      ],
      defaultValue: {
        enabled: true,
        resultsPerPage: 24,
        loadType: 'button',
        loadText: 'Load More',
        buttonStyle: 'btn-inverse-dark',
      },
    },
    {
      label: 'Product Item',
      name: 'productItem',
      component: 'group',
      description: 'Star rating, color variant selector, quick shop',
      fields: [
        {
          label: 'Enable Star Rating',
          name: 'enabledStarRating',
          component: 'toggle',
          toggleLabels: {
            true: 'On',
            false: 'Off',
          },
        },
        {
          label: 'Enable Color Variant Selector',
          name: 'enabledColorSelector',
          component: 'toggle',
          toggleLabels: {
            true: 'On',
            false: 'Off',
          },
        },
        {
          label: 'Enable Color Name On Hover',
          name: 'enabledColorNameOnHover',
          component: 'toggle',
          toggleLabels: {
            true: 'On',
            false: 'Off',
          },
        },
        {
          label: 'Enable Quick Shop',
          name: 'enabledQuickShop',
          component: 'toggle',
          description:
            'Note: Quick shop is hidden on mobile and will only show if the product item has only one variant or multiple variants through a single option, e.g. "Size"',
          toggleLabels: {
            true: 'On',
            false: 'Off',
          },
        },
        {
          label: 'Quick Shop Text (Multi-variant)',
          name: 'quickShopMultiText',
          description: 'Use {{option}} to display the variant option name',
          component: 'text',
        },
        {
          label: 'Quick Shop Text (Single-variant)',
          name: 'quickShopSingleText',
          component: 'text',
        },
      ],
      defaultValue: {
        enabledStarRating: true,
        enabledColorSelector: true,
        enabledColorNameOnHover: false,
        enabledQuickShop: true,
        quickShopMultiText: '+ Quick Add {{option}}',
        quickShopSingleText: '+ Quick Add',
      },
    },
    {
      label: 'Promotion',
      name: 'promotion',
      component: 'group',
      description: 'Promo tile campaigns',
      fields: [
        {
          label: 'Promo Tile Campaigns',
          name: 'campaigns',
          component: 'group-list',
          itemProps: {
            label: '{{item.name}}',
          },
          fields: [
            {
              label: 'Name',
              name: 'name',
              component: 'text',
            },
            {
              label: 'Promo Tiles',
              name: 'promoTiles',
              component: 'group-list',
              itemProps: {
                label: '{{item.position}}: {{item.text.heading}}',
              },
              fields: [
                {
                  label: 'Grid Position',
                  name: 'position',
                  component: 'number',
                  description: 'Where should this promo appear in the grid?',
                },
                {
                  label: 'Grid Span',
                  name: 'span',
                  component: 'number',
                  description: 'How many columns should this promo span?',
                  defaultValue: 1,
                },
                {
                  label: 'Link',
                  name: 'link',
                  component: 'link',
                  description: 'Makes the entire tile a clickable link',
                },
                {
                  label: 'Background Settings',
                  name: 'background',
                  component: 'group',
                  description: 'Background color, image, video, dark overlay',
                  fields: [
                    {
                      label: 'Background Color',
                      name: 'bgColor',
                      component: 'select',
                      options: COLORS,
                    },
                    {
                      label: 'Image Alt',
                      name: 'alt',
                      component: 'text',
                      description: 'Brief description of image',
                    },
                    {
                      label: 'Image',
                      name: 'image',
                      component: 'image',
                      description: 'Overrides background color',
                    },
                    {
                      label: 'Video URL',
                      name: 'videoSrc',
                      component: 'text',
                      description: 'Overrides image. Autoplays once in view',
                    },
                    {
                      label: 'Video Poster Image',
                      name: 'videoPoster',
                      component: 'image',
                      description: 'First frame of video while video loads',
                    },
                    {
                      label: 'Overlay Opacity',
                      name: 'overlayOpacity',
                      component: 'number',
                      description: 'Enter a number between 0 and 100',
                      defaultValue: 50,
                    },
                  ],
                },
                {
                  label: 'Text Settings',
                  name: 'text',
                  component: 'group',
                  description: 'Heading, subtext, text color',
                  fields: [
                    {
                      label: 'Icon',
                      name: 'icon',
                      component: 'image',
                    },
                    {
                      label: 'Supertext',
                      name: 'supertext',
                      component: 'text',
                    },
                    {
                      label: 'Supertext Style',
                      name: 'supertextStyle',
                      component: 'select',
                      options: [
                        {
                          label: 'Standard',
                          value: 'standard',
                        },
                        {
                          label: 'Bubble',
                          value: 'bubble',
                        },
                      ],
                    },
                    {
                      label: 'Heading',
                      name: 'heading',
                      component: 'text',
                    },
                    {
                      label: 'Heading Size',
                      name: 'headingSize',
                      component: 'select',
                      defaultValue: 'standard',
                      options: [
                        {
                          label: 'Standard',
                          value: 'standard',
                        },
                        {
                          label: 'Large',
                          value: 'large',
                        },
                      ],
                    },
                    {
                      label: 'Subtext',
                      name: 'subtext',
                      component: 'textarea',
                    },
                    {
                      label: 'Enable Bottom Accent',
                      name: 'bottomAccentEnabled',
                      component: 'toggle',
                    },
                    {
                      label: 'Bottom Button',
                      name: 'bottomButton',
                      component: 'text',
                    },
                    {
                      label: 'Bottom Button Theme',
                      name: 'bottomButtonTheme',
                      component: 'select',
                      defaultValue: 'primary',
                      options: [
                        {
                          label: 'Light',
                          value: 'light',
                        },
                        {
                          label: 'Primary',
                          value: 'primary',
                        },
                        {
                          label: 'Secondary',
                          value: 'secondary',
                        },
                      ],
                    },
                    {
                      label: 'Bottom Button Variant',
                      name: 'bottomButtonVariant',
                      component: 'select',
                      defaultValue: 'filled',
                      options: [
                        {
                          label: 'Outlined',
                          value: 'outlined',
                        },
                        {
                          label: 'Filled',
                          value: 'filled',
                        },
                      ],
                    },
                    {
                      label: 'Text Color',
                      name: 'textColor',
                      component: 'select',
                      options: COLORS,
                    },
                  ],
                },
              ],
              defaultItem: {
                position: 5,
                aspectRatio: 'aspect-[3/4]',
                background: { bgColor: 'var(--off-white)', darkOverlay: false },
                text: {
                  heading: 'Promo Tile Heading',
                  subtext:
                    'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
                  textColor: 'var(--text)',
                },
              },
            },
            {
              label: 'Collection Handles',
              name: 'collections',
              component: 'list',
              description:
                'Add all collection handles that will run this campaign, e.g. "all", "best-sellers".\n\nTo apply to search results page, add "search"',
              field: {
                component: 'text',
              },
            },
          ],
          defaultItem: {
            enabled: true,
            name: 'Campaign',
            promoTiles: [
              {
                position: 5,
                aspectRatio: 'aspect-[3/4]',
                background: { bgColor: 'var(--off-white)', darkOverlay: false },
                text: {
                  heading: 'Promo Tile Heading',
                  subtext:
                    'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
                  textColor: 'var(--text)',
                },
              },
            ],
            collections: ['all'],
          },
        },
      ],
    },
    {
      label: 'Product Type Grids',
      name: 'productTypeGrids',
      component: 'group-list',
      description:
        'Define how many grid columns per each Product Type (Default: 3)',
      itemProps: {
        label: '{{item.productType}} - {{item.productTypeGridCount}}',
      },
      fields: [
        {
          label: 'Product Type',
          component: 'text',
          name: 'productType',
        },
        {
          label: 'Grid Count',
          component: 'number',
          name: 'productTypeGridCount',
          defaultValue: 3,
        },
      ],
    },
  ],
};
